import React, { useId, useRef } from 'react';
import { motion } from 'framer-motion';

/**
 * Segmented tabs with a sliding indicator.
 * `<Tabs tabs={[{ id, label, count?, icon? }]} value onChange />` — renders the tablist only;
 * render the active panel yourself based on `value`. Arrow keys / Home / End move between tabs.
 */
export default function Tabs({ tabs = [], value, onChange, className = '', size = 'md', ariaLabel = 'Sections' }) {
  const layoutId = useId();
  const listRef = useRef(null);
  const onKeyDown = (e) => {
    if (!tabs.length) return;
    const idx = Math.max(0, tabs.findIndex((t) => t.id === value));
    let next = -1;
    if (e.key === 'ArrowRight') next = (idx + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    if (onChange) onChange(tabs[next].id);
    const btn = listRef.current && listRef.current.querySelectorAll('[role="tab"]')[next];
    if (btn) btn.focus();
  };
  const pad = size === 'sm' ? 'px-3 py-1.5 text-[13px]' : 'px-4 py-2 text-sm';
  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={`relative inline-flex max-w-full gap-1 overflow-x-auto rounded-full border border-line/25 bg-surface-2 p-1 [scrollbar-width:none] ${className}`}
    >
      {tabs.map((t) => {
        const active = t.id === value;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange && onChange(t.id)}
            className={`relative shrink-0 whitespace-nowrap rounded-full font-display font-bold transition-colors ${pad} ${active ? 'text-ink' : 'text-muted hover:text-ink'}`}
          >
            {active && (
              <motion.span
                layoutId={`tab-${layoutId}`}
                className="absolute inset-0 rounded-full bg-surface shadow-soft ring-1 ring-line/20"
                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-1.5">
              {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
              {t.label}
              {t.count != null && (
                <span className={`rounded-full px-1.5 py-px text-[11px] tabular-nums ${active ? 'bg-primary text-primary-ink' : 'bg-ink/10 text-muted'}`}>{t.count}</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
